/**
 * ScenePreview — client-side mirror of the backend scene-count logic
 * so the Create-Your-Own-Story form can show a live "here's what will
 * happen when you click Create" panel as the user types.
 *
 * Kept in sync with `backend/custom_story_service.py`:
 *   • Markers <sceneNN> tags → chunks (case/whitespace/hyphen tolerant)
 *   • Leading prose before the first marker becomes scene 1
 *   • Empty-chunks-only marker soup → falls back to auto
 *   • Auto uses ~1 scene per 50 words, clamped [6, 24]
 */
import { useMemo } from "react";

export const AUTO_SCENE_MIN = 6;
export const AUTO_SCENE_MAX = 24;
const AUTO_WORDS_PER_SCENE = 50;

// Matches <scene1>, <SCENE 2>, <scene-02>, <SCENE_10>, <scene 003>, …
// Whitespace/hyphen/underscore inside the tag is tolerated;
// multi-digit numbers work. We intentionally do NOT match bare
// prose like "In scene 2 of the story…" to avoid false positives.
const SCENE_MARKER_RE = /<\s*scene[\s_-]*\d+\s*>/gi;

/**
 * Split by <sceneNN> markers. Returns an ordered list of chunk
 * strings, or `null` when no usable markers are present (in which
 * case the auto path applies).
 */
export function splitByMarkers(story) {
  const s = (story || "").toString();
  if (!s || !SCENE_MARKER_RE.test(s)) return null;
  // .test() on a global regex advances lastIndex — reset it before
  // splitting to avoid a wonky first-call skip.
  SCENE_MARKER_RE.lastIndex = 0;
  const parts = s
    .split(SCENE_MARKER_RE)
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.length ? parts : null;
}

/**
 * Estimate scene count for Auto mode. Whitespace-splits the story
 * and rounds to `AUTO_WORDS_PER_SCENE`. Clamped to the bounds the
 * backend also enforces.
 */
export function autoSceneCount(story) {
  const s = (story || "").toString().trim();
  if (!s) return AUTO_SCENE_MIN;
  const words = s.split(/\s+/).filter(Boolean).length;
  const guessed = Math.round(words / AUTO_WORDS_PER_SCENE);
  return Math.max(AUTO_SCENE_MIN, Math.min(AUTO_SCENE_MAX, guessed));
}

/**
 * Compute the preview plan for the current form state.
 * Returns { mode, sceneCount, chunks[]?, note? }.
 *
 *   mode="chunks" — user hand-authored breaks with <sceneNN> tags
 *   mode="auto"   — AI decides the count (approx shown for UX)
 *   mode="exact"  — user picked a fixed integer
 */
export function planScenes(story, sceneCountSetting) {
  const chunks = splitByMarkers(story);
  if (chunks) {
    return {
      mode: "chunks",
      sceneCount: chunks.length,
      chunks,
    };
  }
  if (sceneCountSetting === "auto" || sceneCountSetting == null) {
    return {
      mode: "auto",
      sceneCount: autoSceneCount(story),
    };
  }
  return {
    mode: "exact",
    sceneCount: Number(sceneCountSetting) || AUTO_SCENE_MIN,
  };
}

function ChunkPreviewRow({ index, text }) {
  // Keep each preview row short — the point is to reassure the user
  // the split happened where they expected, not to render the full
  // scene text.
  const snippet = text.length > 140 ? `${text.slice(0, 140)}…` : text;
  return (
    <li
      className="flex gap-3 py-1.5 border-b border-zinc-900/60 last:border-b-0"
      data-testid={`scene-preview-chunk-${index}`}
    >
      <span className="font-mono text-[10px] text-yellow-400/70 shrink-0 pt-0.5 w-6">
        {String(index + 1).padStart(2, "0")}
      </span>
      <span className="text-[12px] text-zinc-300 leading-snug">{snippet}</span>
    </li>
  );
}

/**
 * The live panel rendered under the story textarea. Purely
 * presentational — reads from `story` + `sceneCountSetting` and
 * spits out a tiny plan.
 */
export function ScenePreview({ story, sceneCountSetting }) {
  const plan = useMemo(
    () => planScenes(story, sceneCountSetting),
    [story, sceneCountSetting],
  );

  const empty = !story || story.trim().length < 20;

  if (empty) {
    return (
      <div
        data-testid="scene-preview-empty"
        className="mt-2 text-[11px] text-zinc-600 font-mono"
      >
        // scene preview will appear here once your description is at
        least 20 characters…
      </div>
    );
  }

  const headerByMode = {
    chunks: {
      label: "Manual mode",
      accent: "text-yellow-400",
      subtitle: (n) =>
        `${n} scene break${n === 1 ? "" : "s"} detected from your <scene…> tags`,
    },
    auto: {
      label: "Auto mode",
      accent: "text-emerald-400",
      subtitle: (n) =>
        `AI will pick around ${n} scene${n === 1 ? "" : "s"} — actual count is chosen by GPT-5.2`,
    },
    exact: {
      label: "Fixed count",
      accent: "text-sky-400",
      subtitle: (n) => `Exactly ${n} scene${n === 1 ? "" : "s"} will be generated`,
    },
  }[plan.mode];

  return (
    <div
      data-testid="scene-preview"
      data-preview-mode={plan.mode}
      data-preview-count={plan.sceneCount}
      className="mt-3 border border-zinc-800 bg-zinc-950/60 rounded-sm px-4 py-3"
    >
      <div className="flex items-baseline gap-3 mb-2">
        <span
          className={`font-mono text-[10px] uppercase tracking-[0.25em] ${headerByMode.accent}`}
        >
          [{headerByMode.label}]
        </span>
        <span
          className="text-[11px] text-zinc-400"
          data-testid="scene-preview-subtitle"
        >
          {headerByMode.subtitle(plan.sceneCount)}
        </span>
      </div>

      {plan.mode === "chunks" ? (
        <ol className="list-none pl-0 space-y-0">
          {plan.chunks.map((chunk, i) => (
            <ChunkPreviewRow key={i} index={i} text={chunk} />
          ))}
        </ol>
      ) : (
        <div className="text-[11px] text-zinc-500 leading-relaxed">
          {plan.mode === "auto" ? (
            <>
              Tip — insert{" "}
              <code className="text-yellow-400/80 bg-black/40 px-1 rounded">
                &lt;scene02&gt;
              </code>
              ,{" "}
              <code className="text-yellow-400/80 bg-black/40 px-1 rounded">
                &lt;scene03&gt;
              </code>
              , … tags in the description to force a break at that
              exact spot. Without tags, GPT-5.2 picks the beats itself.
            </>
          ) : (
            <>
              Prefer to let the AI decide? Switch{" "}
              <em className="text-yellow-400/80">Scenes</em> back to{" "}
              <em className="text-yellow-400/80">Auto</em>. Or drop{" "}
              <code className="text-yellow-400/80 bg-black/40 px-1 rounded">
                &lt;scene02&gt;
              </code>{" "}
              tags in the description for manual breaks.
            </>
          )}
        </div>
      )}
    </div>
  );
}
